// Copyright © 2025 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { Fragment } from 'react';
import { useIntl } from 'react-intl';
import cx from 'classnames';
import { get } from 'lodash';
import {
  IconButton,
  TableContainer,
  Table as MuiTable,
  TableHead as MuiTableHead,
  TableBody,
  TableRow as MuiTableRow,
  TableCell,
  Typography,
  withStyles,
} from '@material-ui/core';
import StopRoundedIcon from '@material-ui/icons/StopRounded';
import StopOutlinedIcon from '@material-ui/icons/StopOutlined';
import {
  arrayOf, bool, func, number, oneOfType, shape, string,
} from 'prop-types';
import { isNumber } from 'lodash';

import { getI18n } from '../../../../../utils/intl';
import {
  KEYS,
} from '../../../../../constants';
import styles from './styles';

export const getNotDeployedCount = ({ published, pending, deployed, error }) => {
  if(!isNumber(published)) {
    const intl = getI18n(useIntl());
    return intl.getI18nMessage('label.key.store.no.data');
  }
  const notDeployedCount = published - (
    pending + deployed + error
  );
  return (notDeployedCount < 0) ? 0 : notDeployedCount;
};

export const TableHead = ({
  classes = {},
  id,
  rowName,
  cells = [],
}) => (
  <MuiTableHead>
    <MuiTableRow className={classes.tableRow}>
      {(id !== KEYS) &&
        <TableCell scope="row">{rowName}</TableCell>
      }
      {
        cells.map(({ key, value, cellClass, isHidden = false }) =>
          !isHidden &&
          <TableCell
            align="right"
            key={key}
            data-layer7-test={`${id}.${key}`}
            data-apim-test={`${id}.${key}`}
            classes={{ root: cellClass }}
          >
            {value}
          </TableCell>,
        )
      }
    </MuiTableRow>
  </MuiTableHead>
);

TableHead.propTypes = {
  classes: shape({}),
  id: string,
  rowName: string,
  cells: arrayOf(shape({})),
};

export const TableRow = ({
  classes = {},
  id,
  rowName,
  cells = [],
}) => (
  <MuiTableRow>
    {(id !== KEYS) &&
      <TableCell scope="row">{rowName}</TableCell>
    }
    {
      cells.map(({ key, value, onClick, cellClass, isHidden = false }) => (
        !isHidden &&
        <TableCell
          align="right"
          key={key}
          data-layer7-test={`${id}.${key}`}
          data-apim-test={`${id}.${key}`}
          className={cellClass}
        >
          {(onClick && isNumber(value)) ?
            <IconButton className={classes.clickableCell} onClick={onClick}>
              <Typography variant="body1">{value}</Typography>
            </IconButton> :
            <Typography variant="body1">{value}</Typography>
          }
        </TableCell>
      ))
    }
  </MuiTableRow>
);

TableRow.propTypes = {
  classes: shape({}),
  id: string,
  rowName: string,
  cells: arrayOf(shape({})),
};

export const Table = (props) => {
  const {
    classes = {},
    id,
    isProxyDisconnected,
    publishedCount = 0,
    pendingCount = 0,
    deployedCount = 0,
    errorCount = 0,
    keyStoreCount = 0,
    notDeployedCount,
    linkUrl,
    queryParams,
    push,
    keyStoreType,
  } = props;

  const intl = getI18n(useIntl());

  const labelPublished = (id === KEYS) ?
    intl.getI18nMessage('label.proxy.details.page.status.all.keys') :
    intl.getI18nMessage('label.proxy.details.page.status.all.keys');

  const onClickLink = (param) => {
    if(linkUrl) {
      const qParam = get(queryParams, param);
      const link = qParam ? `${linkUrl}${qParam}` : linkUrl;
      push(link);
    }
  };

  const tableHeadCellsDisconnected = [
    {
      key: 'published',
      value: (
        <Fragment>
          <IconButton className={cx(classes.icon, classes.publishedDisconnected)}>
            <StopOutlinedIcon />
          </IconButton>
          {intl.getI18nMessage('label.proxy.details.page.status.all.keys')}
        </Fragment>
      ),
    },
  ];

  const tableHeadCells = [
    {
      key: 'published',
      value: (
        <Fragment>
          <IconButton className={cx(classes.icon, classes.published)}>
            <StopOutlinedIcon />
          </IconButton>
          {labelPublished}
        </Fragment>
      ),
    },
    {
      key: 'notDeployed',
      value: (
        <Fragment>
          <IconButton className={cx(classes.icon, classes.notDeployed)}>
            <StopRoundedIcon />
          </IconButton>
          {intl.getI18nMessage('label.proxy.details.page.status.not.deployed')}
        </Fragment>
      ),
    },
    {
      key: 'pending',
      value: (
        <Fragment>
          <IconButton className={cx(classes.icon, classes.pending)}>
            <StopRoundedIcon />
          </IconButton>
          {intl.getI18nMessage('label.proxy.details.page.status.pending')}
        </Fragment>
      ),
    },
    {
      key: 'deployed',
      value: (
        <Fragment>
          <IconButton className={cx(classes.icon, classes.deployed)}>
            <StopRoundedIcon />
          </IconButton>
          {intl.getI18nMessage('label.proxy.details.page.status.deployed')}
        </Fragment>
      ),
    },
    {
      key: 'error',
      value: (
        <Fragment>
          <IconButton className={cx(classes.icon, classes.error)}>
            <StopRoundedIcon />
          </IconButton>
          {intl.getI18nMessage('label.proxy.details.page.status.error')}
        </Fragment>
      ),
    },
    {
      key: 'keyStore',
      isHidden: keyStoreType === 'REMOTE_KEY_STORE',
      value: (
        <Fragment>
          {intl.getI18nMessage('label.key.store.count')}
        </Fragment>
      ),
    },
  ];

  const totalRowCells = [
    {
      key: 'total.published',
      value: publishedCount,
    },
    {
      key: 'total.not.deployed',
      value: notDeployedCount,
    },
    {
      key: 'total.pending',
      value: pendingCount,
      onClick: () => onClickLink('totalPending'),
    },
    {
      key: 'total.deployed',
      value: deployedCount,
      onClick: () => onClickLink('totalDeployed'),
    },
    {
      key: 'total.error',
      value: errorCount,
      onClick: () => onClickLink('totalError'),
    },
    {
      key: 'total.key.store',
      isHidden: keyStoreType === 'REMOTE_KEY_STORE',
      value: keyStoreCount,
    },
  ];

  return (
    <TableContainer>
      <MuiTable size="small">
        {isProxyDisconnected ?
          <TableHead
            classes={classes}
            id={id}
            rowName={intl.getI18nMessage('label.proxy.details.page.source')}
            cells={tableHeadCellsDisconnected}
          /> :
          <TableHead
            classes={classes}
            id={id}
            rowName={intl.getI18nMessage('label.proxy.details.page.source')}
            cells={tableHeadCells}
          />
        }
        <TableBody>
          <TableRow
            classes={classes}
            id={id}
            rowName={intl.getI18nMessage('label.proxy.details.api.total')}
            cells={isProxyDisconnected ?
              [
                {
                  key: 'total.published',
                  value: publishedCount,
                },
              ] :
              totalRowCells
            }
          />
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
}

export default (
  withStyles(styles)
)(Table);

Table.propTypes = {
  classes: shape({}),
  id: string,
  isProxyDisconnected: bool,
  publishedCount: number,
  pendingCount: number,
  deployedCount: number,
  errorCount: number,
  notDeployedCount: number,
  otkCount: number,
  portalPublishedCount: number,
  portalPendingCount: number,
  portalDeployedCount: number,
  portalErrorCount: number,
  gatewayPublishedCount: number,
  gatewayPendingCount: oneOfType([number, string]),
  gatewayDeployedCount: oneOfType([number, string]),
  gatewayErrorCount: oneOfType([number, string]),
  showBreakdown: bool,
  hideSubtotal: bool,
  onClickLink: func,
  linkUrl: string,
  queryParams: shape({}),
  push: func,
  deploymentType: string,
  notDeployed: string,
  keyStoreCount: number,
  keyStoreType: string,
};
